import DashboardContainer from "components/dashboard/DashboardContainer";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import Tab from "components/DesignSystem/Tab/Tab";
import { ReactNode, useEffect, useMemo } from "react";
import { Route, Switch, useLocation, useRouteMatch } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useDebounce } from "utils/debounce";
import { Search } from "components/DesignSystem/Search/Search";
import { FilingAccordionTable } from "./FilingAccordionTable";
import { SelectFinancialYear } from "components/Task/SelectFinancialYear";
import {
  TaxFiling,
  useGetFilingCountQuery,
  useGetAllFilingsQuery,
  useRecalculateAllFilingTotalsMutation,
} from "store/apis/filing";
import Async from "components/DesignSystem/AsyncComponents/Async";
import dayjs from "dayjs";
import { useModal } from "hooks/useModal";
import { BULK_SALES_TAX_INVOICE_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import {
  usePopulateBulkSalesTaxInvoiceMutation,
  useUploadBulkSalesTaxInvoiceMutation,
  useGetNexusTrackerSummaryQuery,
} from "store/apis/salesTax";
import { NoRegistrationsEmptyState } from "./NoRegistrationsEmptyState";
import { BulkInvoiceUpload } from "components/InvoiceList/BulkInvoiceUpload";
import {
  ALL,
  ACTION_REQUIRED,
  NOT_STARTED,
  IN_CART,
  COMPLETED,
  FILED,
  IN_PROGRESS,
  PROCESSING,
  SUBSCRIBED,
} from "constants/filing";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { EmptySalesTax } from "components/Illustrations/EmptySalesTax";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { FloatingChat } from "components/floatingChat/FloatingChat";
import OutsideClickHandler from "react-outside-click-handler";
import { useDispatch } from "react-redux";
import {
  closeFloatingChat,
  openFloatingChat,
  setToOpenChatId,
} from "store/slices/chat";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { AddFilingModal } from "./AddFilingModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import Loader from "components/DesignSystem/Loader/loader";

export const statusMap = {
  [NOT_STARTED]: "Not started",
  [IN_CART]: "In cart",
  [PROCESSING]: "Processing",
  [FILED]: "Filed",
  [SUBSCRIBED]: "Subscribed",
};
const createColumn = createColumnHelper<TaxFiling>();

export type TabType = "all" | "action_required" | "in_progress" | "completed";

export const isOverdue = (dueDate: string): boolean => {
  return dayjs().isAfter(dayjs(dueDate));
};

const calculateFilingsDue = (filings: TaxFiling[] | undefined) => {
  if (!filings) {
    return {
      numberOfDueSoon: 0,
      numberOfOverDue: 0,
    };
  }
  let numberOfDueSoon = 0;
  let numberOfOverDue = 0;

  const notStartedFilings = filings.filter(
    (filing) => filing.status === NOT_STARTED
  );

  notStartedFilings.forEach((filing) => {
    if (isOverdue(filing.filing_due_date)) {
      numberOfOverDue++;
    } else {
      numberOfDueSoon++;
    }
  });

  return {
    numberOfDueSoon,
    numberOfOverDue,
  };
};

const FillingDuesWarning = ({
  overDue,
  dueSoon,
}: {
  overDue?: number;
  dueSoon?: number;
}) => {
  const getFilingStatusText = () => {
    if (overDue && dueSoon) {
      return `You have ${overDue} filings overdue and ${dueSoon} due soon`;
    }
    if (dueSoon) {
      return `You have ${dueSoon} filings due soon`;
    }
    if (overDue) {
      return `You have ${overDue} filings overdue`;
    }
    return null;
  };

  return (
    <div className="t-flex t-mt-6 t-items-center t-justify-between t-bg-orange-0 t-p-4 t-rounded-md t-border t-border-solid t-border-orange-20">
      <div className="t-flex t-items-start t-gap-2">
        <div className="t-text-orange-80">
          <TriangleWarning />
        </div>
        <div>
          <p className="t-text-subtext-sm t-text-text-100 t-mb-1">
            {getFilingStatusText()}
          </p>
          <p className="t-text-body-sm t-text-text-30 t-m-0">
            Start filing with us for these periods so we can get your returns
            filed on time.
          </p>
        </div>
      </div>
    </div>
  );
};

const ActionRequiredTab = ({
  handleAddClick,
  taxYear,
  searchTerm,
  selectedSeasons,
}: {
  handleAddClick: () => void;
  taxYear: number;
  searchTerm?: string | null;
  selectedSeasons?: string[];
}) => {
  const entityId = useCurrentEntityId();
  const { data, isLoading } = useGetAllFilingsQuery(
    {
      entityId,
      status: ACTION_REQUIRED,
      seasons: taxYear,
      searchTerm,
    },
    {
      skip: !taxYear,
    }
  );
  const { numberOfDueSoon, numberOfOverDue } = calculateFilingsDue(data);

  return (
    <div className="t-w-full t-mb-12">
      <Async.Root
        isLoading={false}
        isSuccess={Boolean(numberOfDueSoon || numberOfOverDue)}
        isEmpty={!Boolean(numberOfDueSoon || numberOfOverDue) && !isLoading}
      >
        <Async.Success>
          <FillingDuesWarning
            dueSoon={numberOfDueSoon}
            overDue={numberOfOverDue}
          />
        </Async.Success>
      </Async.Root>
      <div className="t-flex t-w-full t-mt-4">
        <FilingAccordionTable
          handleAddClick={handleAddClick}
          tab={ACTION_REQUIRED}
          searchTerm={searchTerm}
          selectedSeasons={selectedSeasons}
        />
      </div>
    </div>
  );
};

const AllTab = ({
  searchTerm,
  selectedSeasons,
}: {
  searchTerm?: string | null;
  selectedSeasons?: string[];
}) => {
  return (
    <div className="t-flex t-w-full t-mb-12">
      <FilingAccordionTable
        tab={ALL}
        searchTerm={searchTerm}
        selectedSeasons={selectedSeasons}
      />
    </div>
  );
};

export const FilingEmptyState = ({
  children,
  title = "No sales tax filing added yet",
}: {
  children: ReactNode;
  title?: string;
}) => {
  const emptyStateColumns = useMemo(
    () => [
      createColumn.accessor("state", {
        header: () => <p className="t-m-0 t-pl-3">States</p>,
        size: 25,
      }),
      createColumn.accessor("filing_due_date", {
        header: "Deadline",
        size: 15,
      }),
      createColumn.accessor("total_sales", {
        header: () => <p className="t-text-end t-m-0">Sales</p>,
        size: 15,
      }),
      createColumn.accessor("total_tax_collected", {
        header: () => <p className="t-text-end t-m-0">Tax collected</p>,
        size: 15,
      }),
      createColumn.accessor("status", {
        header: "Status",
        size: 15,
      }),
      createColumn.display({
        header: "Action",
        size: 15,
      }),
    ],
    []
  );

  const data = useMemo(() => [], []);

  const table = useReactTable({
    data,
    getCoreRowModel: getCoreRowModel(),
    columns: emptyStateColumns,
    defaultColumn: {
      minSize: 6,
    },
  });

  return (
    <div className="t-py-5 t-flex t-flex-col">
      <TableUI table={table} />

      <div className="t-flex t-mt-[15%] t-flex-col t-items-center t-justify-center">
        <div>
          <EmptySalesTax />
        </div>
        <p className="t-text-text-100 t-text-body-sm t-mb-0 t-mt-6">{title}</p>
        <p className="t-text-text-30 t-text-body-sm t-mb-0 t-mt-1">
          {children}
        </p>
      </div>
    </div>
  );
};

export const Filings = () => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();

  const { link } = useConstructInternalLink();
  const entityId = useCurrentEntityId();
  const [recalculateAllFilingTotals] = useRecalculateAllFilingTotalsMutation();

  const query = useQuery();
  const { update } = useUpdateQuery();
  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value || null });
  };

  useEffect(() => {
    if (entityId) {
      void recalculateAllFilingTotals({ entityId });
    }
  }, [entityId, recalculateAllFilingTotals]);

  const { data: allData, isLoading: isAllDataLoading } = useGetFilingCountQuery(
    {
      entityId,
      status: ALL,
      searchTerm,
    }
  );

  const selectedSeasons = query.get("seasons");
  const seasonOptions = allData
    ? [
        { label: "All Financial years", value: "all" },
        ...allData.map((item) => ({
          label: String(item.task__season),
          value: String(item.task__season),
        })),
      ]
    : [
        {
          label: selectedSeasons || "All Financial years",
          value: selectedSeasons || "all",
        },
      ];
  const selectedSeasonsArray = selectedSeasons
    ? selectedSeasons.replace("all,", "").split(",")
    : [];

  const { data: actionRequiredData, isLoading: isActionRequiredDataLoading } =
    useGetFilingCountQuery({
      entityId,
      status: ACTION_REQUIRED,
      searchTerm,
    });
  const { data: inProgressData, isLoading: isInProgressDatLoading } =
    useGetFilingCountQuery({
      entityId,
      status: IN_PROGRESS,
      searchTerm,
    });
  const { data: completedData, isLoading: isCompleteDataLoading } =
    useGetFilingCountQuery({
      entityId,
      status: COMPLETED,
      searchTerm,
    });
  const addTransactionModal = useModal();

  const dispatch = useDispatch();
  const { secondary_channel_url: channelId } = useCurrentGroup();

  const onContactSupport = () => {
    addTransactionModal.close();
    dispatch(openFloatingChat());
    dispatch(setToOpenChatId(channelId || ""));
  };

  const currentTaxYear = actionRequiredData?.[0]?.task__season;

  const addFilingModal = useModal();
  const { isAdmin } = useRoleBasedView();

  const { salestax_channel } = useCurrentEntity();

  useMemo(() => {
    if (salestax_channel) {
      dispatch(setToOpenChatId(salestax_channel));
    }
  }, [salestax_channel]);

  const { data: nexusTrackerSummary, isSuccess: isNexusTrackerSummarySuccess } =
    useGetNexusTrackerSummaryQuery({ entityId });
  const hasNoRegistrations =
    isNexusTrackerSummarySuccess && nexusTrackerSummary.registered_count === 0;

  if (hasNoRegistrations) {
    return (
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header className="t-px-10 t-z-header">
          <Header
            v2
            title={
              <div className="t-flex t-items-center t-gap-2 t-h-full">
                Filings
              </div>
            }
          />
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-px-10">
          <NoRegistrationsEmptyState
            registrationsUrl={link(
              `${path.replace("/state-filings", "")}/registrations`
            )}
          />
        </DashboardContainer.Content>
      </DashboardContainer>
    );
  }

  return (
    <Tab.Root asChild defaultValue={pathname} value={link(pathname)}>
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header className="t-px-10 t-z-header">
          <Header
            v2
            title={
              <div className="t-flex t-items-center t-gap-2 t-h-full">
                Filings
              </div>
            }
            bottom={
              <div className="t-flex t-w-full t-flex-col t-gap-3">
                <div className="t-flex t-items-center t-gap-2">
                  <SelectFinancialYear seasons={seasonOptions} />
                  <Search
                    customSize="small"
                    value={searchValue ?? ""}
                    onChange={handleSearchChange}
                  />
                  {/* <Button
                    size="small"
                    customType={!isAdmin ? "primary" : "secondary"}
                    onClick={addTransactionModal.open}
                  >
                    Add sales
                  </Button> */}
                  {/* {isAdmin && (
                    <Button
                      size="small"
                      onClick={addFilingModal.open}
                      customType="primary"
                    >
                      Add filing
                    </Button>
                  )} */}
                </div>
                <Tab.List>
                  <Tab.NavTrigger exact value={`${url}${link("")}`}>
                    <span className="t-text-body-sm ">All</span>
                  </Tab.NavTrigger>
                  <Tab.NavTrigger
                    exact
                    value={`${url}/${link(ACTION_REQUIRED)}`}
                  >
                    <span className="t-text-body-sm ">Action required</span>
                  </Tab.NavTrigger>
                  <Tab.NavTrigger exact value={`${url}/${link(IN_PROGRESS)}`}>
                    <span className="t-text-body-sm ">In progress</span>
                  </Tab.NavTrigger>
                  <Tab.NavTrigger exact value={`${url}/${link(COMPLETED)}`}>
                    <span className="t-text-body-sm ">Completed</span>
                  </Tab.NavTrigger>
                </Tab.List>
              </div>
            }
          />
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-px-10 t-pt-4">
          <Async.Root
            isSuccess={
              !Boolean(
                isAllDataLoading ||
                  isActionRequiredDataLoading ||
                  isCompleteDataLoading ||
                  isInProgressDatLoading
              )
            }
            isEmpty={false}
            isLoading={
              isAllDataLoading ||
              isActionRequiredDataLoading ||
              isCompleteDataLoading ||
              isInProgressDatLoading
            }
            renderOnLoad={
              <div className="t-flex t-min-h-[60vh] t-w-full t-items-center t-justify-center">
                <Loader addClassName="t-w-full" />
              </div>
            }
          >
            <Async.Success>
              <Switch>
                <Route exact path={path}>
                  {allData?.length ? (
                    <AllTab
                      searchTerm={searchTerm}
                      selectedSeasons={selectedSeasonsArray}
                    />
                  ) : (
                    <FilingEmptyState>
                      Get started by adding sales tax registrations to see
                      filings here.
                    </FilingEmptyState>
                  )}
                </Route>
                <Route path={`${path}/action_required`}>
                  {actionRequiredData?.length ? (
                    <ActionRequiredTab
                      taxYear={currentTaxYear!}
                      handleAddClick={addTransactionModal.open}
                      searchTerm={searchTerm}
                      selectedSeasons={selectedSeasonsArray}
                    />
                  ) : (
                    <FilingEmptyState title="No action required">
                      All caught up — nothing needs your attention right now.
                    </FilingEmptyState>
                  )}
                </Route>
                <Route path={`${path}/in_progress`}>
                  {inProgressData?.length ? (
                    <FilingAccordionTable
                      tab={IN_PROGRESS}
                      searchTerm={searchTerm}
                      selectedSeasons={selectedSeasonsArray}
                    />
                  ) : (
                    <FilingEmptyState title="No filings in progress yet">
                      Start a filing or take action on pending items to move
                      them to In Progress.
                    </FilingEmptyState>
                  )}
                </Route>
                <Route path={`${path}/completed`}>
                  {completedData?.length ? (
                    <FilingAccordionTable
                      tab={COMPLETED}
                      searchTerm={searchTerm}
                      selectedSeasons={selectedSeasonsArray}
                    />
                  ) : (
                    <FilingEmptyState title="No completed filings yet">
                      Once you finish your filings, they’ll appear here for your
                      records.
                    </FilingEmptyState>
                  )}
                </Route>
              </Switch>
            </Async.Success>
          </Async.Root>
        </DashboardContainer.Content>

        <BulkInvoiceUpload
          isOpen={addTransactionModal.isOpen}
          close={addTransactionModal.close}
          page="filings"
          onContactSupport={onContactSupport}
          usePopulateMutation={usePopulateBulkSalesTaxInvoiceMutation}
          useUploadMutation={useUploadBulkSalesTaxInvoiceMutation}
          templateUrl={BULK_SALES_TAX_INVOICE_EXCEL_TEMPLATE_URL}
        />
        {salestax_channel && (
          <OutsideClickHandler
            onOutsideClick={() => {
              dispatch(closeFloatingChat());
            }}
          >
            <FloatingChat />
          </OutsideClickHandler>
        )}
        <AddFilingModal
          open={addFilingModal.isOpen}
          onClose={addFilingModal.close}
        />
      </DashboardContainer>
    </Tab.Root>
  );
};
